/**
 * WineChatOverlay — VIN-2026-59 Phase 2 (2026-05-28)
 *
 * A contextual AI chat that slides up as an OVERLAY on top of any
 * screen — including the WOTD sheet — so a user can ask about whatever
 * wine is in front of them without losing their place. Opened via the
 * aiChat.js store (openWineChat(wine)).
 *
 * Two modes, driven by whether a `wine` is in the store:
 *   • Focused:  "Talking about: {wine}" — system prompt centers on that
 *               bottle, plus a light cellar summary so it can answer
 *               "do I have anything similar?" (user picked Wine + light
 *               cellar context).
 *   • General:  no focused wine — acts as a quick cellar assistant. A
 *               link to the full /vinarai Sommelier covers deep modes
 *               (Dine Out, Cellar Analysis).
 *
 * Deliberately lighter than the full Sommelier page: short context,
 * Haiku model, capped history — fast + cheap for "quick question about
 * this bottle." Deep multi-wine analysis stays on /vinarai.
 *
 * Renders at z-index 1000 (above bottom nav z40, FAB z39, action sheet
 * z60, WOTD overlay). Closing returns the user exactly where they were.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { collection, getDocs, query, where, limit } from 'firebase/firestore'
import { db } from '../firebase.js'
import { useTheme } from '../context/ThemeProvider.jsx'
import { useWineChat, closeWineChat } from '../utils/aiChat.js'
import { useScrollLock } from '../hooks/useScrollLock.js'
import { callAnthropicText } from '../utils/aiClient.js'
import { captureException } from '../utils/sentry.js'
// VIN-2026-60 Ship A (2026-05-28): iOS polish for the overlay.
//  • useVisualKeyboardOffset — lift the input above the soft keyboard
//    (the overlay autofocuses, so the keyboard opens every launch).
//  • setOverlayOpen — hide the native alphabet rail under the overlay
//    (the rail is a UIView above the WKWebView, not z-index-aware).
//  • ensureCellarForUser — resolve the cellarId so the AI's cellar
//    context reflects household/merged cellars, not just own-authored wines.
import { useVisualKeyboardOffset } from '../hooks/useVisualKeyboardOffset.js'
import { setOverlayOpen } from '../utils/overlayState.js'
import { ensureCellarForUser } from '../utils/cellars.js'

// ── Light cellar summary (module-cached per uid for the session) ──────
// A trimmed snapshot of the user's cellar so the AI can answer "do I
// have something similar" without the full Sommelier's 150-wine dump.
const _cellarCache = new Map()   // uid → { at, summary }
const CELLAR_TTL = 5 * 60 * 1000

async function loadLightCellarSummary(user) {
  const uid = user?.uid
  if (!uid) return ''
  const cached = _cellarCache.get(uid)
  if (cached && Date.now() - cached.at < CELLAR_TTL) return cached.summary
  try {
    // VIN-2026-60: query by cellarId (matching Home/WOTD) so a household
    // or merged cellar is fully reflected — a userId-only query misses
    // wines authored by a household partner, making the AI say "you
    // don't have anything like this" when the household does. Fall back
    // to userId if the cellarId can't be resolved (fresh account / offline).
    let cellarId = null
    try { cellarId = await ensureCellarForUser(user) } catch { /* fall back to userId */ }
    const winesQuery = cellarId
      ? query(collection(db, 'wines'), where('cellarId', '==', cellarId), limit(60))
      : query(collection(db, 'wines'), where('userId', '==', uid), limit(60))
    const snap = await getDocs(winesQuery)
    const wines = snap.docs.map(d => d.data())
    if (wines.length === 0) {
      const summary = 'The user\'s cellar is currently empty.'
      _cellarCache.set(uid, { at: Date.now(), summary })
      return summary
    }
    // Compact lines: name (vintage) — type, region [score]. Cap at 40
    // so the prompt stays small; sort best-scored first so the most
    // "comparable" wines are always present.
    const lines = wines
      .map(w => ({
        name: w.name || 'Unnamed',
        vintage: w.vintage || '',
        type: w.type || '',
        region: w.region || w.country || '',
        score: Number(w.score ?? w.rating) || 0,
        inInventory: w.inInventory !== false,
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 40)
      .map(w => `• ${w.name}${w.vintage ? ` ${w.vintage}` : ''} — ${[w.type, w.region].filter(Boolean).join(', ')}${w.score ? ` [${w.score}]` : ''}${w.inInventory ? '' : ' (tasted, not in inventory)'}`)
    const summary = `The user's cellar (${wines.length} wines, top ${lines.length} shown):\n${lines.join('\n')}`
    _cellarCache.set(uid, { at: Date.now(), summary })
    return summary
  } catch (err) {
    console.warn('[WineChatOverlay] cellar summary load failed:', err?.message)
    return ''   // non-fatal — the chat still works wine-only
  }
}

function describeWine(w) {
  if (!w) return ''
  const bits = [
    w.producer && `Producer: ${w.producer}`,
    w.name && `Wine: ${w.name}`,
    w.vintage && `Vintage: ${w.vintage}`,
    w.type && `Type: ${w.type}`,
    (w.varietal || w.grape) && `Grape: ${w.varietal || w.grape}`,
    (w.region || w.appellation) && `Region: ${[w.appellation, w.region].filter(Boolean).join(', ')}`,
    w.country && `Country: ${w.country}`,
    (w.score || w.rating) && `Score on file: ${w.score || w.rating}`,
    w.drinkingWindow && `Drinking window: ${w.drinkingWindow}`,
  ].filter(Boolean)
  return bits.join('\n')
}

function buildSystemPrompt({ wine, cellarSummary }) {
  const base = [
    'You are Vinarai, a warm, concise wine companion embedded in a mobile app.',
    'Answer in 2-4 short sentences unless the user asks for more. No purple prose.',
    'Never invent ratings, prices, or facts you are not given. If unsure, say so plainly.',
    'You are a guide, not a substitute for the user\'s own palate.',
  ]
  if (wine) {
    base.push('\nThe user is currently looking at THIS wine — center your answers on it:')
    base.push(describeWine(wine))
  }
  if (cellarSummary) {
    base.push('\nLight context on the user\'s cellar (use only to answer "what do I have like this" / comparisons — do not list it unprompted):')
    base.push(cellarSummary)
  }
  return base.join('\n')
}

function suggestionsFor(wine) {
  if (wine) {
    return [
      'Is this ready to drink or should I hold?',
      'What food pairs with this?',
      'Do I have something similar in my cellar?',
    ]
  }
  return [
    'What should I drink tonight?',
    'Analyze my cellar',
    'Recommend something to buy',
  ]
}

export default function WineChatOverlay({ user }) {
  const { open, wine } = useWineChat()
  const { theme } = useTheme()
  const navigate = useNavigate()
  const [messages, setMessages] = useState([])   // [{ role:'user'|'assistant', content }]
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const cellarRef = useRef('')
  const scrollRef = useRef(null)
  const inputRef = useRef(null)
  // VIN-2026-60: keyboard height (0 when closed) — lifts the input row
  // above the iOS soft keyboard. Hook is a no-op on web / older WebViews.
  const keyboardPx = useVisualKeyboardOffset()

  // (Re)seed when the overlay opens or the focused wine changes. Fresh
  // greeting per open keeps the thread tight + scoped to the wine.
  useEffect(() => {
    if (!open) return
    const greeting = wine
      ? `Ask me anything about the ${wine.name || 'wine'}${wine.vintage ? ` ${wine.vintage}` : ''} — drink window, pairings, or how it stacks up against your cellar.`
      : 'Ask me about your cellar, what to open tonight, or a wine you\'re considering.'
    setMessages([{ role: 'assistant', content: greeting }])
    setInput('')
    // Warm the cellar summary in the background (cellarId-scoped).
    if (user?.uid) loadLightCellarSummary(user).then(s => { cellarRef.current = s })
    // VIN-2026-63 (#129): do NOT autofocus the input on open. Autofocus
    // raised the soft keyboard mid-slide-up, which fought the keyboardPx
    // resize and produced a janky two-step open (sheet settles, then the
    // keyboard shoves it smaller). The overlay now opens cleanly to its
    // full state with the suggestion chips tappable; the keyboard appears
    // only when the user taps the input — a deliberate action where the
    // resize reads as intentional, not jank.
    // wine identity (id or name) is the dependency — reseed on wine swap.
  }, [open, wine?.id, wine?.name, user?.uid])

  // VIN-2026-60: while open, lock body scroll (so the page behind the
  // scrim can't rubber-band on iOS) and broadcast the overlay-open
  // signal so the native alphabet rail hides under us — matching the
  // action sheet + WOTD. Both restored on close/unmount.
  // iOS-safe body scroll-lock (position:fixed) — overflow:hidden alone let
  // the page drag behind the overlay on iOS (#136).
  useScrollLock(open)
  useEffect(() => {
    if (!open) return
    setOverlayOpen('wine-chat', true)
    return () => {
      setOverlayOpen('wine-chat', false)
    }
  }, [open])

  // Keep the message list pinned to the latest turn.
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages, sending])

  if (!open) return null

  const accent = theme.isDark ? 'var(--app-gold)' : 'var(--app-burgundy)'

  const send = async (text) => {
    const content = String(text ?? input).trim()
    if (!content || sending) return
    setInput('')
    const nextMsgs = [...messages, { role: 'user', content }]
    setMessages(nextMsgs)
    setSending(true)
    try {
      const system = buildSystemPrompt({ wine, cellarSummary: cellarRef.current })
      // Send only the conversational turns (drop the seeded greeting so
      // the model doesn't think it already spoke). Cap to last 8 turns
      // for cost/latency.
      const history = nextMsgs
        .filter((m, i) => !(i === 0 && m.role === 'assistant'))
        .slice(-8)
        .map(m => ({ role: m.role, content: m.content }))
      const reply = await callAnthropicText({
        system,
        messages: history,
        maxTokens: 500,
        temperature: 0.3,
      })
      setMessages(m => [...m, { role: 'assistant', content: reply || 'Sorry — I didn\'t catch that. Try again?' }])
    } catch (err) {
      console.warn('[WineChatOverlay] send failed:', err?.message)
      captureException(err, { tags: { feature: 'wineChatOverlay', op: 'send' }, extra: { hasWine: !!wine } })
      setMessages(m => [...m, { role: 'assistant', content: err?.message?.includes('busy')
        ? 'Vinarai is busy right now — give it a moment and try again.'
        : 'Something went wrong reaching Vinarai. Try again in a moment.' }])
    } finally {
      setSending(false)
    }
  }

  return (
    <div
      role="dialog" aria-modal="true" aria-label="Ask Vinarai"
      // VIN-2026-60: paddingBottom = keyboard height lifts the bottom-
      // anchored sheet to sit just above the iOS keyboard. Because the
      // sheet's height is a % of this wrap's (now shorter) content box,
      // it shrinks proportionally rather than clipping its header — the
      // messages area just gets a little shorter while the keyboard is up.
      style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', paddingBottom: keyboardPx, transition: 'padding-bottom 0.2s ease' }}
    >
      <style>{`@keyframes aiSlideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}@keyframes aiFade{from{opacity:0}to{opacity:1}}@keyframes aiDots{0%,80%,100%{opacity:.3}40%{opacity:1}}`}</style>
      {/* Scrim */}
      <div onClick={closeWineChat} style={{ position: 'absolute', inset: 0, background: 'rgba(10,8,12,0.6)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', animation: 'aiFade 0.18s ease' }} />

      {/* Sheet */}
      <div style={{
        position: 'relative', zIndex: 2,
        background: theme.isDark
          ? 'linear-gradient(180deg, #1C1117, #0E0C12)'
          : 'linear-gradient(180deg, #FAF5EC, #F1EADD)',
        borderRadius: '26px 26px 0 0',
        borderTop: `1px solid ${theme.borders.emphasis}`,
        height: '78%', display: 'flex', flexDirection: 'column',
        animation: 'aiSlideUp 0.24s cubic-bezier(0.22,1,0.36,1)',
        boxShadow: '0 -12px 40px rgba(0,0,0,0.4)',
      }}>
        {/* Header */}
        <div style={{ padding: '14px 18px 12px', borderBottom: `1px solid ${theme.borders.subtle}`, flexShrink: 0 }}>
          <button type="button" onClick={closeWineChat} aria-label="Close" style={{ display: 'flex', justifyContent: 'center', width: '100%', padding: '2px 0 12px', background: 'none', border: 'none', cursor: 'pointer' }}>
            <div style={{ width: 42, height: 5, borderRadius: 999, background: theme.isDark ? 'rgba(196,150,90,0.3)' : 'rgba(123,32,64,0.25)' }} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 18, color: accent }}>✦</span>
              {wine ? (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  background: theme.isDark ? 'rgba(196,150,90,0.1)' : 'rgba(123,32,64,0.07)',
                  border: `1px solid ${theme.borders.subtle}`, borderRadius: 999, padding: '5px 11px',
                  fontFamily: 'Raleway, sans-serif', fontSize: 11, fontWeight: 600, color: accent,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent, flexShrink: 0 }} />
                  {wine.name || 'This wine'}{wine.vintage ? ` ${wine.vintage}` : ''}
                </span>
              ) : (
                <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 19, fontWeight: 600, color: theme.text.primary }}>Ask Vinarai</span>
              )}
            </div>
          </div>
        </div>

        {/* Mode bar (VIN-2026-63 #130) — the deeper Sommelier modes were
            buried in a tiny text link at the very bottom; surface them as a
            prominent row right under the header. Each deep-links into that
            mode on /vinarai (?mode=… read by Sommelier on mount). */}
        <div style={{ display: 'flex', gap: 8, padding: '10px 16px 2px', flexShrink: 0 }}>
          {[
            { key: 'chat', label: 'Sommelier', icon: <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /> },
            { key: 'dineout', label: 'Dine Out', icon: <><path d="M4 3v6a2 2 0 0 0 4 0V3M6 11v10M14 3c-1.5 0-3 1.8-3 5s1.5 4 3 4m0-9v18" /></> },
            { key: 'cellaranalysis', label: 'Analysis', icon: <><path d="M4 4v16h16" /><rect x="7" y="11" width="3" height="6" /><rect x="12" y="7" width="3" height="10" /><rect x="17" y="13" width="3" height="4" /></> },
          ].map(m => (
            <button key={m.key} type="button" onClick={() => { closeWineChat(); navigate(`/vinarai?mode=${m.key}`) }} style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
              padding: '9px 4px', borderRadius: 12,
              background: theme.isDark ? 'rgba(196,150,90,0.07)' : 'rgba(123,32,64,0.05)',
              border: `1px solid ${theme.borders.subtle}`, cursor: 'pointer', color: accent,
              fontFamily: 'Raleway, sans-serif', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
            }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{m.icon}</svg>
              {m.label}
            </button>
          ))}
        </div>

        {/* Messages */}
        <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {messages.map((m, i) => (
            <div key={i} style={{
              maxWidth: '85%',
              alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
              background: m.role === 'user'
                ? 'linear-gradient(135deg, #7B2040, #3A0D18)'
                : (theme.isDark ? 'rgba(244,239,231,0.06)' : 'rgba(255,255,255,0.7)'),
              color: m.role === 'user' ? '#F4EFE7' : theme.text.primary,
              border: m.role === 'user' ? 'none' : `1px solid ${theme.borders.subtle}`,
              borderRadius: 16,
              borderBottomRightRadius: m.role === 'user' ? 5 : 16,
              borderBottomLeftRadius: m.role === 'user' ? 16 : 5,
              padding: '11px 14px',
              fontFamily: 'Raleway, sans-serif', fontSize: 13.5, lineHeight: 1.55,
              whiteSpace: 'pre-wrap',
            }}>
              {m.content}
            </div>
          ))}
          {sending && (
            <div style={{ alignSelf: 'flex-start', display: 'flex', gap: 4, padding: '11px 14px' }}>
              {[0, 1, 2].map(d => (
                <span key={d} style={{ width: 7, height: 7, borderRadius: '50%', background: accent, animation: `aiDots 1.2s ${d * 0.16}s infinite ease-in-out` }} />
              ))}
            </div>
          )}
        </div>

        {/* Suggestion chips — only before the first user turn */}
        {messages.filter(m => m.role === 'user').length === 0 && !sending && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '0 18px 10px', flexShrink: 0 }}>
            {suggestionsFor(wine).map(s => (
              <button key={s} type="button" onClick={() => send(s)} style={{
                fontFamily: 'Raleway, sans-serif', fontSize: 11.5, color: accent,
                background: theme.isDark ? 'rgba(196,150,90,0.08)' : 'rgba(123,32,64,0.05)',
                border: `1px solid ${theme.borders.subtle}`, borderRadius: 999,
                padding: '8px 13px', cursor: 'pointer', textAlign: 'left',
              }}>{s}</button>
            ))}
          </div>
        )}

        {/* Input row */}
        <div style={{ padding: '12px 16px max(22px, env(safe-area-inset-bottom))', display: 'flex', gap: 10, borderTop: `1px solid ${theme.borders.subtle}`, flexShrink: 0 }}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
            placeholder={wine ? 'Ask about this wine…' : 'Ask Vinarai…'}
            enterKeyHint="send"
            style={{
              flex: 1, height: 46, borderRadius: 14,
              background: theme.isDark ? 'rgba(244,239,231,0.06)' : 'rgba(255,255,255,0.8)',
              border: `1px solid ${theme.borders.default}`,
              padding: '0 16px', color: theme.text.primary,
              fontFamily: 'Raleway, sans-serif', fontSize: 14, outline: 'none',
            }}
          />
          <button type="button" onClick={() => send()} disabled={sending || !input.trim()} aria-label="Send" style={{
            width: 46, height: 46, borderRadius: 14, flexShrink: 0,
            background: (sending || !input.trim()) ? 'rgba(196,150,90,0.3)' : 'linear-gradient(135deg, #C4965A, #9c7338)',
            border: 'none', cursor: (sending || !input.trim()) ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1208" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4z"/></svg>
          </button>
        </div>

        {/* (VIN-2026-63 #130) the old bottom "Open full Sommelier" text
            link was removed — the mode bar under the header now surfaces
            Sommelier / Dine Out / Cellar Analysis prominently. */}
      </div>
    </div>
  )
}
